import { Field, Select, IFormValidation } from '@busybusy/webapp-react-ui';
import classNames from 'classnames';
import { ClassName } from 'types/ClassName';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import './DayOfMonthRangeFormField.scss';
import _ from 'lodash';
import { createNumberWithSuffix, removeSuffiFromNumber } from 'utils/numberUtils';

interface IDayOfMonthRangeFormFieldProps {
  name: string;
  form: any;
  defaultValue?: string[]; // Size 2 list.
  firstProps?: ISelectProps;
  secondProps?: ISelectProps;
  validations?: Array<IFormValidation<string[]>>;
  onChange?: (value: (string | null)[]) => void;
  className?: ClassName;
}

interface ISelectProps {
  disabled?: boolean;
  hasArrow?: boolean;
  onBlur?: () => void;
  onChange?: (value: string) => void;
  onClick?: () => void;
  onFocus?: () => void;
  onKeyDown?: () => void;
  placeholder?: string;
}

const DayOfMonthRangeFormField: FunctionComponent<IDayOfMonthRangeFormFieldProps> = (props) => {
  const { name, form, defaultValue, className, validations, firstProps, secondProps, onChange } = props;

  const [t] = useTranslation();

  function getFirstOptions(items: string[] | null) {
    if (items && items.length === 2) {
      const secondNumber = removeSuffiFromNumber(items[1]);
      return _.times(secondNumber - 1, (num) => createNumberWithSuffix(num + 1)).map((suffixed) => ({
        value: suffixed,
        label: t(suffixed),
      }));
    } else {
      return _.times(31, (num) => createNumberWithSuffix(num + 1)).map((suffixed) => ({
        value: suffixed,
        label: t(suffixed),
      }));
    }
  }

  function getSecondOptions(items: string[] | null) {
    if (items && items.length === 2) {
      const firstNumber = removeSuffiFromNumber(items[0]);
      return _.times(31 - firstNumber, (num) => createNumberWithSuffix(firstNumber + num + 1)).map((suffixed) => ({
        value: suffixed,
        label: t(suffixed),
      }));
    } else {
      return _.times(31, (num) => createNumberWithSuffix(num + 1)).map((suffixed) => ({
        value: suffixed,
        label: t(suffixed),
      }));
    }
  }

  function renderField({ ...fieldProps }) {
    let items: string[] | null = null;
    if (form.state.data[name]) {
      items = form.state.data[name].slice();
    }

    function onFirstChange(value: string) {
      fieldProps.handleChange([value, items ? items[1] : null], firstProps ? firstProps.onChange : undefined);
      onChange?.([value, items ? items[1] : null]);
    }

    function onSecondChange(value: string) {
      fieldProps.handleChange([items ? items[0] : null, value], secondProps ? secondProps.onChange : undefined);
      onChange?.([items ? items[0] : null, value]);
    }

    return (
      <div className="field-container">
        <Select
          className="first-field"
          {...firstProps}
          onChange={onFirstChange}
          value={items ? items[0] : ''}
          options={getFirstOptions(items)}
        />
        <Select
          className="second-field"
          {...secondProps}
          onChange={onSecondChange}
          value={items ? items[1] : ''}
          options={getSecondOptions(items)}
        />
      </div>
    );
  }

  const classes = classNames(
    {
      'day-of-month-range-form-field': true,
    },
    className
  );

  return (
    <Field
      form={form}
      name={name}
      defaultValue={defaultValue}
      validations={validations}
      className={classes}
      render={renderField}
    />
  );
};

export default DayOfMonthRangeFormField;
